// ProofingTimerPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Timer as TimerIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';

const PRESET_TIMES = [3, 5, 8, 12]; // minutes

const ProofingTimerPanel = () => {
  const [activeTimers, setActiveTimers] = useState({});
  const [customMinutes, setCustomMinutes] = useState('');

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const createTimer = useCallback((minutes) => {
    const id = Date.now();
    const totalSeconds = minutes * 60;
    
    setActiveTimers(prev => ({
      ...prev,
      [id]: {
        id,
        minutes,
        totalSeconds,
        remainingSeconds: totalSeconds,
        isRunning: true, // automatically start the timer
        isComplete: false,
      }
    }));
  }, []);

  const createCustomTimer = useCallback(() => {
    const minutes = parseFloat(customMinutes);
    if (minutes > 0 && minutes <= 999) { // Reasonable limit
      createTimer(minutes);
      setCustomMinutes(''); // Clear input after creating timer
    }
  }, [customMinutes, createTimer]);

  const handleCustomInputKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      createCustomTimer();
    }
  }, [createCustomTimer]);

  const toggleTimer = useCallback((id) => {
    setActiveTimers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        isRunning: !prev[id].isRunning,
      }
    }));
  }, []);

  const resetTimer = useCallback((id) => {
    setActiveTimers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        remainingSeconds: prev[id].totalSeconds,
        isRunning: true, // automatically start the timer when reset
        isComplete: false,
      }
    }));
  }, []);

  const removeTimer = useCallback((id) => {
    setActiveTimers(prev => {
      const newTimers = { ...prev };
      delete newTimers[id];
      return newTimers;
    });
  }, []);

  // Timer countdown effect
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTimers(prev => {
        const newTimers = { ...prev };
        let hasChanges = false;

        Object.keys(newTimers).forEach(id => {
          const timer = newTimers[id];
          if (timer.isRunning && timer.remainingSeconds > 0) {
            newTimers[id] = {
              ...timer,
              remainingSeconds: timer.remainingSeconds - 1,
            };
            hasChanges = true;

            // Check if timer completed
            if (newTimers[id].remainingSeconds === 0) {
              newTimers[id].isRunning = false;
              newTimers[id].isComplete = true;
              
              // Play notification sound (browser notification)
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(`${timer.minutes} minute proofing timer completed!`, {
                  icon: '🍞',
                  body: 'Your dough is ready for the next step.',
                });
              }
            }
          }
        });

        return hasChanges ? newTimers : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const getProgress = (timer) => {
    return ((timer.totalSeconds - timer.remainingSeconds) / timer.totalSeconds) * 100;
  };

  const formatMinutesLabel = (minutes) => {
    if (minutes % 1 === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${minutes.toFixed(1)} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  return (
    <Box sx={{ width: 280, p: 2, height: '100vh', overflow: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TimerIcon color="primary" />
        <Typography variant="h6" color="primary">
          Proofing Timers
        </Typography>
      </Box>

      {/* Preset Timer Buttons */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Start:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PRESET_TIMES.map((minutes) => (
            <Button
              key={minutes}
              variant="outlined"
              size="small"
              onClick={() => createTimer(minutes)}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              {minutes}m
            </Button>
          ))}
        </Box>
      </Box>

      {/* Custom Timer Input */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Custom Timer:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            type="text"
            value={customMinutes}
            onChange={(e) => setCustomMinutes(e.target.value)}
            onKeyPress={handleCustomInputKeyPress}
            InputProps={{
              endAdornment: <InputAdornment position="end">min</InputAdornment>,
            }}
            inputProps={{
              min: 0.1,
              max: 999,
              step: 0.1,
            }}
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="contained"
            size="large"
            onClick={createCustomTimer}
            disabled={!customMinutes || parseFloat(customMinutes) <= 0}
            startIcon={<AddIcon />}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Start
          </Button>
        </Box>
      </Box>

      {/* Active Timers */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Object.values(activeTimers).map((timer) => (
          <Card
            key={timer.id}
            variant="outlined"
            sx={{
              bgcolor: timer.isComplete ? 'success.light' : 'background.paper',
              borderColor: timer.isComplete ? 'success.main' : 'divider',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  {formatMinutesLabel(timer.minutes)}
                </Typography>
                <Chip
                  label={timer.isComplete ? 'Complete!' : formatTime(timer.remainingSeconds)}
                  color={timer.isComplete ? 'success' : timer.isRunning ? 'primary' : 'default'}
                  size="small"
                  sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                />
              </Box>

              <LinearProgress
                variant="determinate"
                value={getProgress(timer)}
                sx={{
                  mb: 2,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    bgcolor: timer.isComplete ? 'success.main' : 'primary.main',
                  },
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => toggleTimer(timer.id)}
                  disabled={timer.isComplete}
                  color={timer.isRunning ? 'warning' : 'primary'}
                >
                  {timer.isRunning ? <PauseIcon /> : <PlayIcon />}
                </IconButton>
                
                <IconButton
                  size="small"
                  onClick={() => resetTimer(timer.id)}
                  color="secondary"
                >
                  <RefreshIcon />
                </IconButton>
                
                <IconButton
                  size="small"
                  onClick={() => removeTimer(timer.id)}
                  color="error"
                >
                  <StopIcon />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {Object.keys(activeTimers).length === 0 && (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center', 
          color: 'text.secondary',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed'
        }}>
          <TimerIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            No active timers.
            <br />
            Start one above!
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="caption" color="info.contrastText" display="block" sx={{ mb: 1 }}>
          💡 Tip:
        </Typography>
        <Typography variant="body2" color="info.contrastText" sx={{ fontSize: '0.75rem' }}>
          Proofing times vary based on temperature and humidity. Use these as starting points and adjust based on your dough's appearance and feel.
        </Typography>
      </Box>
    </Box>
  );
};

export default ProofingTimerPanel;